import { IconButton, Icon } from "@mui/material";
import { useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { MoreHoriz } from "@mui/icons-material";
import {
  IChargingStationAvailabilityModel,
  IChargingStationTriggerModel,
} from "@/services/models/chargingStation";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ChargingStationNotificationType } from "@/common/enums/enums";

export interface ITableActionMenuModel<TRecord = Record<string, any>> {
  title: (record: TRecord | undefined) => string;
  icon: (record: TRecord | undefined) => any;
  iconColor?: any;
  visible?: (record: TRecord | undefined) => boolean;
  disabled?: (record: TRecord | undefined) => boolean;
  onClick?: (record: TRecord | undefined, event?: any) => void;
  dynamicColor?: boolean;
  "data-cy"?: string;
  hasSubMenu?: boolean;
  hasTrigger?: boolean;
  type?: any;
  group?: string;
  destructive?: boolean;
  children?:
    | ITableActionMenuModel<TRecord>[]
    | ((record: TRecord | undefined) => ITableActionMenuModel<TRecord>[]);
}
export const TableActionMenu = (props: {
  menus: ITableActionMenuModel<any>[];
  record?: Record<string, any>;
  styles?: any;
  availabilityHandler: (
    chargingStation: any,
    availabilityParams: IChargingStationAvailabilityModel
  ) => void;
  notificationHandler: (
    chargingStation: any,
    resetParams: IChargingStationTriggerModel
  ) => void;
}) => {
  const [isActionMenu, setActionMenu] = useState<null | HTMLElement>(null);
  const [subMenuAnchor, setSubMenuAnchor] = useState<null | HTMLElement>(null);
  const [availabilityType, setAvailabilityType] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [selectedTriggerItem, setSelectedTriggerItem] =
    useState<boolean>(false);
  const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null); // Track active menu

  const isMenuOpen = Boolean(isActionMenu);
  const isSubMenuOpen = Boolean(subMenuAnchor);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setActionMenu(null);
    setSubMenuAnchor(null);
    setActiveMenuIndex(null); // Reset active menu item
    setSelectedItem(null);
    setSelectedTriggerItem(false);
  };

  const handleSubMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    type: string,
    index: number,
    isTrigger: boolean
  ) => {
    setAvailabilityType(type);
    setSubMenuAnchor(event.currentTarget);
    setActiveMenuIndex(index); // Keep track of active menu item
    setSelectedTriggerItem(isTrigger);
  };

  const handleSubMenuClose = () => {
    setSubMenuAnchor(null);
    setSelectedTriggerItem(false);
  };

  const handleSubMenuSelect = (connectorId: number, connectorLabel: string) => {
    if (props.record) {
      if (selectedTriggerItem) {
        props.notificationHandler(props.record, {
          args: {
            connectorId,
            requestedMessage:
              ChargingStationNotificationType.STATUS_NOTIFICATION,
          },
        });
        setSelectedItem(connectorLabel);
      } else {
        props.availabilityHandler(props.record, {
          args: {
            connectorId,
            type: availabilityType,
          },
        });
        setSelectedItem(connectorLabel);
      }
    }
    handleSubMenuClose();
    handleMenuClose();
  };

  return (
    <div>
      <IconButton
        size="small"
        aria-controls={isMenuOpen ? "basic-menu" : undefined}
        className={props.styles.datatableButton}
        aria-haspopup="true"
        color="primary"
        aria-expanded={isMenuOpen ? "true" : undefined}
        onClick={handleMenuOpen}
      >
        <MoreHoriz className={props.styles.datatableIcon} />
      </IconButton>

      {/* Main Menu */}
      <Menu
        id="basic-menu"
        anchorEl={isActionMenu}
        open={isMenuOpen}
        onClose={handleMenuClose}
        MenuListProps={{ "aria-labelledby": "basic-button" }}
      >
        {props.menus.map((action, index) =>
          !action.visible ||
          (action.visible && action.visible(props.record)) ? (
            <MenuItem
              key={`menu-item-${index}`}
              disabled={action.disabled && action.disabled(props.record)}
              onClick={(event) =>
                action.hasSubMenu
                  ? handleSubMenuOpen(
                      event,
                      action.type,
                      index,
                      action?.hasTrigger ? true : false
                    )
                  : action.onClick?.(props.record, event)
              } // Open submenu on click
              sx={{
                fontSize: "14px",
                minHeight: "48px",
                cursor: "pointer",
                display: "flex",
                backgroundColor:
                  activeMenuIndex === index ? "#E0E9FA" : "transparent",
                // Keep active state
                "&:hover": { backgroundColor: "#E0E9FA" },
              }}
            >
              <Icon
                color={action.iconColor}
                data-cy={action["data-cy"]}
                component={action.icon(props.record)}
                sx={{
                  marginRight: "10px",
                  fontSize: "20px",
                  color: action.iconColor,
                }}
              />
              {action.title(props.record)}

              {/* Show arrow only for the active menu */}
              {action.hasSubMenu && activeMenuIndex === index && (
                <ArrowForwardIcon
                  style={{
                    marginLeft: "100px",
                    color: "#2469F3",
                    width: "12px",
                    height: "12px",
                  }}
                />
              )}
            </MenuItem>
          ) : null
        )}
      </Menu>

      {/* Sub-Menu */}
      <Menu
        id="sub-menu"
        anchorEl={subMenuAnchor}
        open={isSubMenuOpen}
        onClose={handleSubMenuClose}
        sx={{
          marginLeft: "280px",
          marginTop: "-30px",
        }}
      >
        {/* Fix: The Menu component doesn't accept a Fragment as a child.Consider providing an array instead.
        Ticket: EVES-4276
        Author: Abhishek Bharti
        Date: 12/01/2026 */}
        {props.record?.connectors && [
          <MenuItem
            key={0}
            onClick={() => handleSubMenuSelect(0, "All")}
            sx={{
              backgroundColor:
                selectedItem === "All" ? "#E0E9FA" : "transparent",
              "&:hover": { backgroundColor: "#E0E9FA" },
            }}
          >
            All
          </MenuItem>,
          ...(props.record?.connectors || []).map(
            (connector: any, index: number) => {
              const connectorLabel = `Connector ${index + 1}`
              return (
                <MenuItem
                  key={index + 1}
                  onClick={() => handleSubMenuSelect(index + 1, connectorLabel)}
                  sx={{
                    backgroundColor:
                      selectedItem === connectorLabel
                        ? "#E0E9FA"
                        : "transparent",
                    "&:hover": { backgroundColor: "#E0E9FA" },
                  }}
                >
                  {connectorLabel}
                </MenuItem>
              );
            }
          ),
        ]}
      </Menu>
    </div>
  );
};
